import styled, { css } from "styled-components";
import materialUI from "material-UI";
import color from "constants/color";

// Os ícones do projeto têm tamanhos intrínsecos diferentes (20x20, 24x18,
// 13x12...) e cor fixa embutida — ora em `fill`, ora em `stroke`. Os helpers
// abaixo padronizam tamanho e cor para as colunas ficarem uniformes.
const iconSize = (size: number) => css`
  svg {
    width: ${size}px;
    height: ${size}px;
    flex: 0 0 ${size}px;
  }
`;

// Faz o ícone herdar a cor do contexto (`color`), cobrindo fill e stroke.
const iconTint = (tint = "currentColor") => css`
  svg path[fill]:not([fill="none"]) {
    fill: ${tint};
  }

  svg path[stroke] {
    stroke: ${tint};
  }
`;

export const Container = styled.div`
  height: 100vh;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const Header = styled.header`
  height: 74px;
  flex: 0 0 74px;
  background: ${color.White};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 32px;
`;

export const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const IconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #eef4ff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Title = styled.h2`
  font-family: Inter-Medium;
  font-size: 20px;
  line-height: 24px;
  color: ${color.gray900};
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Content = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 16px 20px 0;
  overflow-x: hidden;
`;

export const FixedSection = styled.div`
  flex: 0 0 auto;
  /* matches ScrollArea scrollbar gutter so columns stay aligned */
  padding-right: 8px;
`;

export const ScrollArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;
  padding-bottom: 24px;
  scrollbar-gutter: stable;
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 12px 0 0;

  &.stages {
    grid-template-columns: repeat(7, minmax(0, 1fr));
  }

  @media (max-width: 1024px) {
    gap: 8px;
  }
`;

export const HierarchyRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;

  &.three {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

export const Autocomplete = styled(materialUI.Autocomplete)`
  background: ${color.White};

  .MuiOutlinedInput-root {
    border-radius: 8px;
    background: ${color.White};
    height: 40px;
    box-sizing: border-box;
    fieldset {
      border-radius: 8px;
      border-color: #d9e1ec;
    }
  }

  .MuiInputBase-input {
    padding: 0 14px !important;
  }

  .Mui-focused {
    fieldset {
      border-color: ${color.primary} !important;
    }
  }

  .Mui-disabled {
    background-color: rgb(242, 244, 248);
  }
`;

export const TextField = styled(materialUI.TextField)``;

export const SummaryCard = styled.button`
  position: relative;
  min-height: 92px;
  border-radius: 8px;
  border: 1px solid #dce6f2;
  background: ${color.White};
  padding: 16px;
  text-align: left;
  cursor: pointer;
  transition: transform 0.12s, box-shadow 0.12s, filter 0.12s, opacity 0.12s;

  &:hover {
    box-shadow: 0px 4px 10px rgba(16, 24, 40, 0.08);
    transform: translateY(-1px);
  }

  &.success {
    background: #e9f7ec;
    border-color: #bfe7c8;
    color: #2e7d32;
  }

  &.warning {
    background: #fff3df;
    border-color: #ffdba7;
    color: #ed6c02;
  }

  &.danger {
    background: #fde8ed;
    border-color: #ffc7d1;
    color: #d32f2f;
  }

  &.info {
    background: #e3f2ff;
    border-color: #b8dcff;
    color: #2196f3;
  }

  /* Estados abaixo das cores: mesma especificidade, precisam vir depois para
     sobrescrever border-color (e o box-shadow do :hover). */

  /* Card escolhido como filtro de métrica. */
  &.selected {
    border-color: ${color.primary};
    box-shadow: 0px 0px 0px 1px ${color.primary};
  }

  /* Demais cards enquanto um está selecionado. */
  &.muted {
    filter: grayscale(0.9);
    opacity: 0.6;
  }
`;

// Selo de check no canto do card selecionado.
export const SummaryCheck = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 100%;
  background: ${color.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 12px;
    height: 12px;
  }
`;

// Ícone da métrica + ícone do tipo de agendamento, lado a lado.
// Ambos herdam a cor do card (definida pelas classes de cor do SummaryCard).
export const SummaryIcons = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  ${iconTint()}

  /* Só o ícone da métrica — o do tipo é menor e vem dentro do TypeIcon. */
  > svg {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
  }
`;

// Ícone do tipo de agendamento — um pouco menor que o da métrica.
export const TypeIcon = styled.span`
  display: inline-flex;
  align-items: center;
  ${iconSize(16)}
`;

export const SummaryNumber = styled.strong`
  display: flex;
  align-items: baseline;
  font-family: Inter-Medium;
  font-size: 28px;
  line-height: 32px;
  margin-top: 8px;

  @media (max-width: 1024px) {
    font-size: 24px;
    line-height: 28px;
  }
`;

// Percentual ao lado do número do card — texto simples, sem pill.
export const SummaryPercent = styled.span`
  margin-left: 8px;
  font-family: Inter-Medium;
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  opacity: 0.75;
`;

export const SummaryLabel = styled.span`
  display: block;
  font-family: Inter;
  font-size: 12px;
  line-height: 16px;
  margin-top: 2px;
  overflow-wrap: break-word;
  word-break: break-word;
  hyphens: auto;

  @media (max-width: 1024px) {
    font-size: 11px;
    line-height: 14px;
  }
`;

export const SearchWrap = styled.div`
  height: 42px;
  border: 1px solid #d9e1ec;
  background: ${color.White};
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  margin: 16px 0;

  svg {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
  }

  svg path {
    fill: #98a2b3;
  }
`;

export const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  font-family: Inter;
  font-size: 14px;
  color: ${color.gray900};

  &::placeholder {
    color: #98a2b3;
  }
`;

export const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 14px;

  &.stages {
    grid-template-columns: 1fr;
  }
`;

export const HeaderGroup = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) repeat(3, minmax(64px, 82px));
  gap: 10px;
  align-items: center;
  padding: 0 18px;

  &.stages {
    grid-template-columns: minmax(0, 1fr) repeat(7, minmax(64px, 82px));
  }

  &.four {
    grid-template-columns: minmax(0, 1fr) repeat(4, minmax(60px, 78px));
  }
`;

export const HeaderCell = styled.span`
  font-family: Inter-Medium;
  font-size: 11px;
  line-height: 13px;
  color: #98a2b3;
  text-align: center;

  /* Ícone da métrica: mesmo tamanho em todas as colunas e cor da própria
     coluna (as classes de cor abaixo definem o \`color\`). */
  ${iconSize(18)}
  ${iconTint()}

  svg {
    vertical-align: middle;
  }

  &:first-child {
    text-align: left;
  }

  /* Rótulo da coluna de identificação: texto cinza, ícone azul. */
  &.label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow-wrap: break-word;
    word-break: break-word;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    ${iconSize(16)}
    ${iconTint(color.primary)}
  }

  &.success {
    color: #2e7d32;
  }

  &.warning {
    color: #ed6c02;
  }

  &.danger {
    color: #d32f2f;
  }

  &.info {
    color: #2196f3;
  }
`;

export const BrokerList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 18px;

  &.stages {
    grid-template-columns: 1fr;
  }
`;

export const BrokerCard = styled.div`
  min-height: 72px;
  border: 1px solid #dfe6ef;
  border-radius: 8px;
  background: ${color.White};
  display: grid;
  grid-template-columns: minmax(0, 1fr) repeat(3, minmax(64px, 82px));
  gap: 10px;
  align-items: center;
  padding: 12px 18px;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.06);

  &.stages {
    grid-template-columns: minmax(0, 1fr) repeat(7, minmax(64px, 82px));
  }

  &.four {
    grid-template-columns: minmax(0, 1fr) repeat(4, minmax(60px, 78px));
  }

  &.clickable {
    cursor: pointer;
    transition: border-color 0.12s, box-shadow 0.12s, opacity 0.12s;
  }

  &.selected {
    border-color: ${color.primary};
    box-shadow: 0px 0px 0px 1px ${color.primary} inset;

    ${() => BrokerName} {
      color: ${color.primary};
    }
  }

  /* Quando há uma linha selecionada, as demais ficam esmaecidas. */
  &.dimmed {
    opacity: 0.45;
  }
`;

// Badge circular de porcentagem exibido ao lado do valor na pill de métrica.
export const PercentPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(46, 125, 50, 0.16);
  color: #2e7d32;
  font-family: Inter-Medium;
  font-weight: 600;
  font-size: 11px;
  line-height: 14px;
`;

export const BrokerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

export const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 100%;
  background: #eef4ff;
  overflow: hidden;
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Avatar destacado (azul) da linha selecionada.
// O ícone de usuário traz um rect de fundo próprio, que some para o azul do
// avatar aparecer.
export const AvatarSelected = styled(Avatar)`
  background: ${color.primary};
  ${iconTint(color.White)}

  svg rect {
    fill: transparent;
  }
`;

export const BrokerName = styled.p`
  font-family: Inter-Medium;
  font-size: 14px;
  line-height: 18px;
  color: ${color.gray900};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const BrokerLeads = styled.p`
  font-family: Inter;
  font-size: 11px;
  line-height: 14px;
  color: #667085;
`;

export const Metric = styled.button`
  height: 34px;
  min-width: 74px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Inter-Medium;
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  color: #344054;
  cursor: pointer;
  transition: filter 0.12s, transform 0.12s, opacity 0.12s, box-shadow 0.12s;

  &:hover {
    filter: brightness(0.98);
    transform: translateY(-1px);
  }

  &.success {
    background: #e9f7ec;
    color: #2e7d32;
  }

  &.warning {
    background: #fff3df;
    color: #ed6c02;
  }

  &.danger {
    background: #fde8ed;
    color: #d32f2f;
  }

  &.info {
    background: #e3f2ff;
    color: #2196f3;
  }

  /* Coluna correspondente ao card de métrica selecionado. */
  &.highlight {
    box-shadow: 0px 0px 0px 2px ${color.primary};
  }

  /* Demais colunas enquanto um card está selecionado. */
  &.muted {
    opacity: 0.35;
  }
`;

export const Empty = styled.p`
  font-family: Inter;
  font-size: 14px;
  color: #667085;
  text-align: center;
  padding: 48px 0;
`;

export const LoadMore = styled.button`
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  background: #eaefff;
  color: ${color.primary};
  font-family: Inter-Medium;
  font-size: 13px;
  display: block;
  margin: 24px auto 0;
  cursor: pointer;
`;
